import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { CommonStudent, CommonSession, CommonEvaluation, CommonChapter } from '@codidae/common-types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from 'components/button/button';
import Card from 'components/card/card';
import TimeRequestButton from 'components/children-home/time-request-button';
import EnterPinView from 'components/enter-pin-view/enter-pin-view';
import Flex from 'components/flex/flex';
import { useUserContext } from 'contexts/user-context';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FaExclamationTriangle, FaEye, FaLock, FaPlay, FaTrash, FaChevronDown } from 'react-icons/fa';
import { HiOutlineSwitchHorizontal } from 'react-icons/hi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTimeRequests } from 'services/time-request';
import { deleteEvaluation } from 'services/evaluations';
import { createSessionFromEvaluation } from 'services/session';
import { ChildrenDashboardTab } from 'views/children/dashboard';
import { SegmentEventType, track } from 'segment';
import { getColorFromFieldId } from 'utils/colors';
import { toast } from 'react-hot-toast';
import { useEvaluations } from 'hooks/use-evaluations';
import { ChatbotContext } from 'contexts/chatbot-context';
import BlockerPlugin from 'utils/BlockerPlugin';
import styles from './children-home.module.scss';

/* eslint-disable-next-line */
export interface ChildrenHomeProps {
  children: CommonStudent;
  blockerConfigured?: boolean;
  setCurrentTab?: (tab: ChildrenDashboardTab) => void;
}

export function ChildrenHome({ children, blockerConfigured, setCurrentTab }: ChildrenHomeProps) {
  const queryClient = useQueryClient();
  const [timeLeft, setTimeLeft] = useState(0);
  const [dailyUsage, setDailyUsage] = useState<number>();
  const [isTimeAccordionOpen, setIsTimeAccordionOpen] = useState(true);
  const { setChatbotContext } = useContext(ChatbotContext);

  const { data: timeRequests, refetch } = useQuery({
    queryKey: ['getTimeRequests'],
    queryFn: () => getTimeRequests(),
    refetchInterval: 30000,
    staleTime: 30000,
  });
  const { 
    evaluations, 
  } = useEvaluations(children.id);

  const [parentWarning, setParentWarning] = useState(false);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('from_parent') === 'true') {
      setParentWarning(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { parentalControlSettings } = useUserContext();

  const maxDailyTime = useMemo(
    () =>
      (parentalControlSettings?.maxDailyTime ?? 0) +
      (timeRequests?.reduce((acc, tr) => acc + (tr.awardedMinutes ?? 0), 0) ?? 0),
    [parentalControlSettings, timeRequests],
  );

  useEffect(() => {
    BlockerPlugin.getLockTimeLeft().then((res) => {
      setTimeLeft(res.timeLeft);
    });
    BlockerPlugin.getDailyUsage().then((res) => {
      setDailyUsage(res.usage);
    });

    const setupListener = async () => {
      const listener = await App.addListener('resume', () => {
        BlockerPlugin.getLockTimeLeft().then((res) => {
          setTimeLeft(res.timeLeft);
        });
        BlockerPlugin.getDailyUsage().then((res) => {
          setDailyUsage(res.usage);
        });
        refetch();
      });

      return listener;
    };

    let listenerRef: any = null;
    setupListener().then(listener => {
      listenerRef = listener;
    });

    return () => {
      if (listenerRef) {
        listenerRef.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  const maxDailyTimeReached = useMemo(
    () => (dailyUsage ?? 0) >= maxDailyTime && maxDailyTime !== 0,
    [dailyUsage, maxDailyTime],
  );

  const [accessBlockerPopup, setAccessBlockerPopup] = useState(false);
  const navigate = useNavigate();

  const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return 'Pas de date';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Date invalide';
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const createSessionFromEvaluationMutation = useMutation<
    CommonSession,
    Error,
    { evaluationId: number } 
  >({
    mutationFn: (variables) => createSessionFromEvaluation(variables.evaluationId),
    onSuccess: (data, variables) => {
      const { evaluationId } = variables;
      // eslint-disable-next-line object-shorthand
      track(SegmentEventType.START_QUIZ_FROM_EVALUATION, { 
        children_id: children.id, 
        evaluationId 
      });
      if (data?.id) {
        navigate(`/children/session/${data.id}?unlock=true`);
      } else {
        console.warn('[ChildrenHome] Session non créée ou terminée immédiatement.');
      }
    },
    onError: (error) => {
      console.error('[ChildrenHome] createSessionFromEvaluationMutation onError:', error);
      toast.error('Erreur lors du démarrage du quiz');
    }
  });

  const deleteEvaluationMutation = useMutation<void, Error, number>({
      mutationFn: deleteEvaluation,
      onSuccess: (_, deletedId) => {
          console.log(`[ChildrenHome] Évaluation ${deletedId} supprimée avec succès.`);
          queryClient.invalidateQueries({ queryKey: ['getEvaluations', children.id] });
      },
      onError: (error, deletedId) => {
          console.error(`[ChildrenHome] Erreur lors de la suppression de l'évaluation ${deletedId}:`, error);
          toast.error("Erreur lors de la suppression de l'évaluation.");
      }
  });
  
  const upcomingEvaluations = useMemo(() => {
    const allEvals = evaluations || [];
    
    // Le filtrage est maintenant fait côté backend par getEvaluationsForChild.
    // On peut donc directement utiliser allEvals (qui ne contient que les bonnes évals).
    
    // Logique pour dédoublonner (gardée au cas où, même si moins probable)
    const uniqueEvalsMap = new Map<number, CommonEvaluation>();
    allEvals.forEach(evaluation => {
      uniqueEvalsMap.set(evaluation.id, evaluation);
    });
    
    return Array.from(uniqueEvalsMap.values());
  }, [evaluations]);

  // Fonction pour gérer le clic sur une évaluation
  const handleEvaluationClick = (evaluation: CommonEvaluation) => {
    const requestId = (evaluation as any).chatbotRequest?.id;
    console.log('[ChildrenHome] Clic sur évaluation traité par handleEvaluationClick:', { evaluationId: evaluation.id, requestId });
    if (requestId && setCurrentTab) {
      // eslint-disable-next-line object-shorthand
      setChatbotContext({
        requestId,
        source: 'evaluation_click',
        timestamp: Date.now(),
      });
      console.log('[ChildrenHome] Contexte chatbot mis à jour via useContext.');
      
      setCurrentTab('chatbot'); 
      console.log('[ChildrenHome] Redirection vers chatbot déclenchée via handleEvaluationClick.');
    } else {
      console.warn(`[ChildrenHome] Impossible d'ouvrir le chat depuis handleEvaluationClick, requestId ou setCurrentTab manquant.`);
      toast.error("Impossible d'ouvrir la conversation associée.");
    }
  };

  // --- AJOUT : État pour l'ID à surligner ---
  const [highlightedEvalId, setHighlightedEvalId] = useState<number | null>(null);

  // --- AJOUT : Effet pour lire sessionStorage et définir l'ID à surligner ---
  useEffect(() => {
    const idToHighlight = sessionStorage.getItem('highlightEvalId');
    console.log('[ChildrenHome] Checking sessionStorage for highlightEvalId:', idToHighlight);
    if (idToHighlight) {
      const parsedId = parseInt(idToHighlight, 10);
      if (!Number.isNaN(parsedId)) {
        console.log('[ChildrenHome] Highlighting evaluation ID:', parsedId);
        setHighlightedEvalId(parsedId);
        sessionStorage.removeItem('highlightEvalId'); // Nettoyer après lecture

        // Timer pour retirer le surlignage après 3 secondes
        const timer = setTimeout(() => {
          console.log('[ChildrenHome] Removing highlight for ID:', parsedId);
          setHighlightedEvalId(null);
        }, 3000);

        // Nettoyage du timer si le composant est démonté
        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, []); // Exécuter seulement au montage

  if (accessBlockerPopup) {
    return (
      <EnterPinView
        onValidate={() => {
          if (Capacitor.getPlatform() === 'ios') {
            navigate(`/blocker/ios/${children.id}`);
          } else {
            navigate(`/blocker/android/${children.id}`);
          }
        }}
        onCancel={() => {
          setAccessBlockerPopup(false);
        }}
      />
    );
  }

  return (
    <Flex 
      direction="column" 
      width="100%" 
      height="100%" 
      style={{ overflow: 'hidden' }} // Conforme aux styles globaux
    >
      {/* Conteneur de contenu scrollable */}
      <div 
        style={{ 
          flex: 1,
          overflowY: 'scroll', // Important: force le scroll ici
          WebkitOverflowScrolling: 'touch', // Pour un meilleur scroll sur iOS
          paddingTop: 110, // Espace pour le header
          paddingBottom: 150, // Espace pour la barre d'onglets
          height: '100%',
          width: '100%',
        }}
      >
        {/* Contenu normal */}
        <Flex 
          direction="column" 
          gap={20} 
          pad={20} 
          width="100%" 
          style={{ maxWidth: '100%', boxSizing: 'border-box' }}
        >
          {blockerConfigured === false && !parentWarning && (
            <Card className={`${styles.card} ${styles.warningCard}`} style={{ width: '100%' }}>
              <Flex width="100%" align="center" gap={15} style={{ marginBottom: 10 }}>
                <FaExclamationTriangle className={styles.exclamationTriangle} />
                <div className={styles.warningText}>{`Altus n'est pas configuré !`}</div>
              </Flex>
              <Button color="black" onClick={() => setAccessBlockerPopup(true)} icon={<FaLock />}>
                CONFIGURER MAINTENANT
              </Button>
            </Card>
          )}

          {parentWarning && (
            <Card className={`${styles.card} ${styles.parentWarningCard}`} style={{ width: '100%' }}>
              <Flex width="100%" align="center" gap={15} style={{ marginBottom: 10 }}>
                <FaEye className={styles.icon} />
                <div className={styles.warningText}>Vous visitez le mode enfant</div>
              </Flex>
              <Button
                color="purple"
                onClick={async () => {
                  await Preferences.set({
                    key: 'phone_owner',
                    value: 'parent',
                  });
                  navigate(`/children/${children.id}`, { replace: true });
                }}
                icon={<HiOutlineSwitchHorizontal />}
                style={{ width: 'calc(100% - 30px)', margin: '0 15px' }}
              >
                REVENIR AU MODE PARENT
              </Button>
              <Flex direction="column" gap={10} width="100%" style={{ marginTop: 20 }}>
                <div>C&apos;est le téléphone de votre enfant ?</div>
                <Button 
                  color="black" 
                  onClick={() => setAccessBlockerPopup(true)} 
                  icon={<FaLock />}
                  style={{ width: 'calc(100% - 30px)', margin: '0 15px' }}
                >
                  CONFIGURER ALTUS
                </Button>
              </Flex>
            </Card>
          )}

          <div className={styles.timeAccordion}>
            <div 
              className={styles.timeAccordionSummary}
              onClick={() => setIsTimeAccordionOpen(!isTimeAccordionOpen)}
            >
              <span className={styles.timeAccordionLabel}>Temps restant</span>
              <span className={styles.timeAccordionValue}>
                {timeLeft > 0 ? `${timeLeft} min` : 'Épuisé'} 
                <FaChevronDown className={`${styles.timeAccordionIcon} ${isTimeAccordionOpen ? styles.timeAccordionIconOpen : ''}`} />
              </span>
            </div>
            
            {isTimeAccordionOpen && (
              <div className={styles.timeAccordionDetails}>
                {dailyUsage !== undefined && (
                  <div className={styles.timeInfoRow}>
                    <span className={styles.timeInfoLabel}>Temps utilisé aujourd&apos;hui</span>
                    <span className={styles.timeInfoValue}>{dailyUsage} minutes</span>
                  </div>
                )}
                {maxDailyTime > 0 && (
                  <div className={styles.timeInfoRow}>
                    <span className={styles.timeInfoLabel}>Temps maximum autorisé</span>
                    <span className={styles.timeInfoValue}>{maxDailyTime} minutes</span>
                  </div>
                )}
                {maxDailyTimeReached && <TimeRequestButton childId={children.id} />}
              </div>
            )}
          </div>

          {(!upcomingEvaluations || upcomingEvaluations.length === 0) ? (
            <Flex 
              direction="column" 
              align="center" 
              gap={20} 
              style={{ marginTop: 40 }}
              className={styles.noEvaluationsContainer}
              width="100%"
            >
              <div style={{ fontSize: 18, color: '$primary800', textAlign: 'center' }}>
                Aucune évaluation pour le moment !
              </div>
              <Button
                color="primary"
                onClick={() => {
                  if (setCurrentTab) {
                    setChatbotContext({
                      requestId: null, 
                      source: 'create_evaluation',
                      timestamp: Date.now()
                    });
                    setCurrentTab('chatbot');
                  } else {
                    navigate('/children/chatbot');
                  }
                }}
              >
                Créer une évaluation
              </Button>
            </Flex>
          ) : (
            <Card className={styles.evaluationsCard}>
              <div className={styles.evaluationsTitle}>Prochaines Évaluations</div>
              <p className={styles.evaluationsHint}>Cliquez sur une évaluation pour discuter avec Altus</p>
              
              <div className={styles.evaluationsGrid}>
                {upcomingEvaluations
                  .sort((a, b) => {
                      const dateA = a.date ? new Date(a.date).getTime() : Infinity;
                      const dateB = b.date ? new Date(b.date).getTime() : Infinity;
                      return dateA - dateB;
                  })
                  .map((evaluation) => (
                      <div 
                        key={evaluation.id} 
                        className={`${styles.evaluationItem} ${evaluation.id === highlightedEvalId ? styles.highlighted : ''}`}
                        onClick={() => handleEvaluationClick(evaluation)} 
                      >
                        <div className={styles.evaluationDetails} 
                            style={{ borderLeft: `5px solid ${getColorFromFieldId(evaluation.field?.id)}` }}>
                           <span className={styles.evaluationDate}>{formatDate(evaluation.date)}</span>
                          {evaluation.field && <span className={styles.evaluationField}>{evaluation.field.name}</span>}
                          <span className={styles.evaluationChapters}>
                            {evaluation.chapters && evaluation.chapters.length > 0 ? (
                              <ul>
                                {evaluation.chapters.slice(0, 3).map((chap: CommonChapter) => (
                                  <li key={chap.id}>{chap.name}</li>
                                ))}
                                {evaluation.chapters.length > 3 && (
                                  <li style={{ color: '#888', fontStyle: 'italic' }}>
                                    + {evaluation.chapters.length - 3} autres
                                  </li>
                                )}
                              </ul>
                            ) : 'Aucun chapitre sélectionné'}
                          </span>
                        </div>
                        
                        <div className={styles.evaluationActions}>
                          {evaluation.chapters && evaluation.chapters.length > 0 && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <Button
                                outline
                                icon={<FaPlay />}
                                onClick={() => {
                                  createSessionFromEvaluationMutation.mutate({ evaluationId: evaluation.id });
                                }}
                                disabled={createSessionFromEvaluationMutation.isPending}
                                className={styles.iconButton}
                                color="primary"
                              >
                                {null}
                              </Button>
                            </div>
                          )}
                          <div onClick={(e) => e.stopPropagation()}>
                            <Button
                              outline
                              color="red" 
                              icon={<FaTrash />}
                              onClick={() => {
                                console.log(`[ChildrenHome] Tentative de suppression de l'évaluation ${evaluation.id}`);
                                deleteEvaluationMutation.mutate(evaluation.id);
                              }}
                              disabled={deleteEvaluationMutation.isPending}
                              className={styles.iconButton}
                            >
                              {null}
                            </Button>
                          </div>
                        </div>
                      </div>
                  ))}
              </div>
            </Card>
          )}
        </Flex>
      </div>
    </Flex>
  );
}

export default ChildrenHome;
